import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Building2,
  FolderKanban,
  FolderTree,
  Plus,
  Pencil,
  Trash2,
  Shield,
  Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPortfolioForm = {
  org_id: '',
  name: '',
  description: '',
  status: 'active',
};

const emptySubPortfolioForm = {
  org_id: '',
  portfolio_id: '',
  name: '',
  description: '',
  status: 'active',
};

function RecordCard({ title, subtitle, badges = [], children, onEdit, onDelete }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {badges.map((badge) => (
              <Badge key={badge.label} variant="outline" className={badge.className}>
                {badge.label}
              </Badge>
            ))}
          </div>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

export default function PortfolioRecordsPage() {
  const { user, org, hasRole } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [subPortfolios, setSubPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [subPortfolioDialogOpen, setSubPortfolioDialogOpen] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [editingSubPortfolio, setEditingSubPortfolio] = useState(null);
  const [portfolioForm, setPortfolioForm] = useState(emptyPortfolioForm);
  const [subPortfolioForm, setSubPortfolioForm] = useState(emptySubPortfolioForm);

  const isAdmin = hasRole(['superadmin', 'platform_admin']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const orgQuery = selectedOrg !== 'all' ? `?org_id=${selectedOrg}` : '';
      const [organizationsRes, portfoliosRes, subPortfoliosRes] = await Promise.all([
        isAdmin ? axios.get(`${API}/organizations`) : Promise.resolve({ data: org ? [org] : [] }),
        axios.get(`${API}/portfolios${orgQuery}`),
        axios.get(`${API}/sub-portfolios${orgQuery}`),
      ]);
      setOrganizations(organizationsRes.data);
      setPortfolios(portfoliosRes.data);
      setSubPortfolios(subPortfoliosRes.data);
    } catch (error) {
      console.error('Failed to fetch portfolio records:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, org, selectedOrg]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const visiblePortfolios = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return portfolios.filter((record) => {
      if (!query) return true;
      return (
        record.name?.toLowerCase().includes(query) ||
        record.org_name?.toLowerCase().includes(query) ||
        record.added_by_user_name?.toLowerCase().includes(query)
      );
    });
  }, [portfolios, searchTerm]);

  const visibleSubPortfolios = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return subPortfolios.filter((record) => {
      if (!query) return true;
      return (
        record.name?.toLowerCase().includes(query) ||
        record.portfolio_name?.toLowerCase().includes(query) ||
        record.org_name?.toLowerCase().includes(query) ||
        record.added_by_user_name?.toLowerCase().includes(query)
      );
    });
  }, [subPortfolios, searchTerm]);

  const availableOrganizations = organizations.length > 0 ? organizations : org ? [org] : [];
  const orgScopedPortfolios = portfolios.filter((record) => {
    if (subPortfolioForm.org_id) return record.org_id === subPortfolioForm.org_id;
    if (selectedOrg !== 'all') return record.org_id === selectedOrg;
    return true;
  });

  const openCreatePortfolio = () => {
    setEditingPortfolio(null);
    setPortfolioForm({
      ...emptyPortfolioForm,
      org_id: selectedOrg !== 'all' ? selectedOrg : org?.id || '',
    });
    setPortfolioDialogOpen(true);
  };

  const openEditPortfolio = (record) => {
    setEditingPortfolio(record);
    setPortfolioForm({
      org_id: record.org_id || '',
      name: record.name || '',
      description: record.description || '',
      status: record.status || 'active',
    });
    setPortfolioDialogOpen(true);
  };

  const openCreateSubPortfolio = () => {
    setEditingSubPortfolio(null);
    setSubPortfolioForm({
      ...emptySubPortfolioForm,
      org_id: selectedOrg !== 'all' ? selectedOrg : org?.id || '',
    });
    setSubPortfolioDialogOpen(true);
  };

  const openEditSubPortfolio = (record) => {
    setEditingSubPortfolio(record);
    setSubPortfolioForm({
      org_id: record.org_id || '',
      portfolio_id: record.portfolio_id || '',
      name: record.name || '',
      description: record.description || '',
      status: record.status || 'active',
    });
    setSubPortfolioDialogOpen(true);
  };

  const savePortfolio = async () => {
    if (!portfolioForm.org_id || !portfolioForm.name.trim()) {
      toast.error('Organization and portfolio name are required');
      return;
    }
    try {
      if (editingPortfolio) {
        await axios.patch(`${API}/portfolios/${editingPortfolio.id}`, portfolioForm);
      } else {
        await axios.post(`${API}/portfolios`, portfolioForm);
      }
      setPortfolioDialogOpen(false);
      setPortfolioForm(emptyPortfolioForm);
      fetchData();
    } catch (error) {
      console.error('Failed to save portfolio:', error);
      toast.error(error.response?.data?.detail || 'Failed to save portfolio');
    }
  };

  const saveSubPortfolio = async () => {
    if (!subPortfolioForm.org_id || !subPortfolioForm.portfolio_id || !subPortfolioForm.name.trim()) {
      toast.error('Organization, parent portfolio, and sub-portfolio name are required');
      return;
    }
    try {
      if (editingSubPortfolio) {
        await axios.patch(`${API}/sub-portfolios/${editingSubPortfolio.id}`, subPortfolioForm);
      } else {
        await axios.post(`${API}/sub-portfolios`, subPortfolioForm);
      }
      setSubPortfolioDialogOpen(false);
      setSubPortfolioForm(emptySubPortfolioForm);
      fetchData();
    } catch (error) {
      console.error('Failed to save sub-portfolio:', error);
      toast.error(error.response?.data?.detail || 'Failed to save sub-portfolio');
    }
  };

  const deletePortfolio = async (record) => {
    if (!window.confirm(`Delete portfolio "${record.name}"?`)) return;
    try {
      await axios.delete(`${API}/portfolios/${record.id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete portfolio:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete portfolio');
    }
  };

  const deleteSubPortfolio = async (record) => {
    if (!window.confirm(`Delete sub-portfolio "${record.name}"?`)) return;
    try {
      await axios.delete(`${API}/sub-portfolios/${record.id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete sub-portfolio:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete sub-portfolio');
    }
  };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-500">Only platform administrators can manage portfolio records right now.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Portfolios</h1>
            <p className="text-slate-500">
              Manage portfolio and sub-portfolio master data before assigning them to assets.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={openCreatePortfolio}>
              <FolderKanban className="w-4 h-4 mr-2" />
              Add Portfolio
            </Button>
            <Button onClick={openCreateSubPortfolio}>
              <FolderTree className="w-4 h-4 mr-2" />
              Add Sub-portfolio
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Organizations Covered</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">{new Set(portfolios.map((r) => r.org_id)).size}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Portfolios</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">{portfolios.length}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Sub-portfolios</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">{subPortfolios.length}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by record, org, or added by..."
                  className="pl-10"
                />
              </div>
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger className="w-full lg:w-[260px]">
                  <SelectValue placeholder="Filter organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All organizations</SelectItem>
                  {availableOrganizations.map((organization) => (
                    <SelectItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="portfolios" className="space-y-4">
          <TabsList>
            <TabsTrigger value="portfolios">Portfolios</TabsTrigger>
            <TabsTrigger value="sub-portfolios">Sub-portfolios</TabsTrigger>
          </TabsList>

          <TabsContent value="portfolios">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Portfolio Master</CardTitle>
                <CardDescription>
                  Admins can create for any organization. Added by is auto-assigned to the org admin when available.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading ? (
                  <p className="text-slate-500">Loading portfolio records...</p>
                ) : visiblePortfolios.length === 0 ? (
                  <p className="text-slate-500">No portfolios found.</p>
                ) : (
                  visiblePortfolios.map((record) => (
                    <RecordCard
                      key={record.id}
                      title={record.name}
                      subtitle={record.description}
                      badges={[
                        { label: record.org_name || 'No org', className: 'bg-slate-100 text-slate-700 border-slate-200' },
                        { label: `Added by ${record.added_by_user_name || '-'}`, className: 'bg-sky-50 text-sky-700 border-sky-200' },
                        { label: record.status || 'active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                      ]}
                      onEdit={() => openEditPortfolio(record)}
                      onDelete={() => deletePortfolio(record)}
                    >
                      <div className="text-sm text-slate-500 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span>{record.org_name || 'Unassigned organization'}</span>
                      </div>
                    </RecordCard>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sub-portfolios">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Sub-portfolio Master</CardTitle>
                <CardDescription>
                  Each sub-portfolio belongs to a parent portfolio and will drive the asset dropdown choices.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading ? (
                  <p className="text-slate-500">Loading sub-portfolio records...</p>
                ) : visibleSubPortfolios.length === 0 ? (
                  <p className="text-slate-500">No sub-portfolios found.</p>
                ) : (
                  visibleSubPortfolios.map((record) => (
                    <RecordCard
                      key={record.id}
                      title={record.name}
                      subtitle={record.description}
                      badges={[
                        { label: record.portfolio_name || 'No parent portfolio', className: 'bg-violet-50 text-violet-700 border-violet-200' },
                        { label: record.org_name || 'No org', className: 'bg-slate-100 text-slate-700 border-slate-200' },
                        { label: `Added by ${record.added_by_user_name || '-'}`, className: 'bg-sky-50 text-sky-700 border-sky-200' },
                      ]}
                      onEdit={() => openEditSubPortfolio(record)}
                      onDelete={() => deleteSubPortfolio(record)}
                    >
                      <div className="text-sm text-slate-500">
                        Parent portfolio: <span className="font-medium text-slate-700">{record.portfolio_name || '-'}</span>
                      </div>
                    </RecordCard>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={portfolioDialogOpen} onOpenChange={setPortfolioDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPortfolio ? 'Edit Portfolio' : 'Add Portfolio'}</DialogTitle>
            <DialogDescription>Platform admins can create records for any organization.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Organization</Label>
              <Select
                value={portfolioForm.org_id || 'unselected'}
                disabled={Boolean(editingPortfolio)}
                onValueChange={(value) =>
                  setPortfolioForm((state) => ({ ...state, org_id: value === 'unselected' ? '' : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unselected">Select organization</SelectItem>
                  {availableOrganizations.map((organization) => (
                    <SelectItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Portfolio Name</Label>
              <Input
                value={portfolioForm.name}
                onChange={(e) => setPortfolioForm((state) => ({ ...state, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={portfolioForm.description}
                onChange={(e) => setPortfolioForm((state) => ({ ...state, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={portfolioForm.status}
                onValueChange={(value) => setPortfolioForm((state) => ({ ...state, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={savePortfolio}>{editingPortfolio ? 'Save Changes' : 'Create Portfolio'}</Button>
              <Button variant="outline" onClick={() => setPortfolioDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={subPortfolioDialogOpen} onOpenChange={setSubPortfolioDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSubPortfolio ? 'Edit Sub-portfolio' : 'Add Sub-portfolio'}</DialogTitle>
            <DialogDescription>Select the parent portfolio first, then name the sub-portfolio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Organization</Label>
              <Select
                value={subPortfolioForm.org_id || 'unselected'}
                disabled={Boolean(editingSubPortfolio)}
                onValueChange={(value) =>
                  setSubPortfolioForm((state) => ({
                    ...state,
                    org_id: value === 'unselected' ? '' : value,
                    portfolio_id: '',
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unselected">Select organization</SelectItem>
                  {availableOrganizations.map((organization) => (
                    <SelectItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Parent Portfolio</Label>
              <Select
                value={subPortfolioForm.portfolio_id || 'unselected'}
                onValueChange={(value) =>
                  setSubPortfolioForm((state) => ({
                    ...state,
                    portfolio_id: value === 'unselected' ? '' : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent portfolio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unselected">Select parent portfolio</SelectItem>
                  {orgScopedPortfolios.map((record) => (
                    <SelectItem key={record.id} value={record.id}>
                      {record.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sub-portfolio Name</Label>
              <Input
                value={subPortfolioForm.name}
                onChange={(e) => setSubPortfolioForm((state) => ({ ...state, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={subPortfolioForm.description}
                onChange={(e) => setSubPortfolioForm((state) => ({ ...state, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={subPortfolioForm.status}
                onValueChange={(value) => setSubPortfolioForm((state) => ({ ...state, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={saveSubPortfolio}>
                {editingSubPortfolio ? 'Save Changes' : 'Create Sub-portfolio'}
              </Button>
              <Button variant="outline" onClick={() => setSubPortfolioDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
